// Team stats view helpers — sorting, position ordering, and season average formatting.
//
// All pure functions, testable without React. The sorting logic handles three cases:
//  - Position sort: groups by position (GKP → DEF → MID → FWD), then alphabetical
//  - Name sort: alphabetical by last name, then first name
//  - Stat sort: numeric by stat value using the string accessor pattern
import type { StatValue } from '@frontend/core/lib/domains/reporting'
import { getStatValue } from '@frontend/core/lib/domains/reporting/types'
import type { UnitSystem } from '@frontend/core/lib/domains/units/types'
import { formatStatValue } from '../../lib/format-cell'
import type { ReportingSportConfig } from '../../lib/sport-config'
import type { ReportingUnitType } from '../../lib/sport-config/types'

export type SortDir = 'asc' | 'desc'

export type StatInput = StatValue | number | null | undefined

/** Minimum shape a player row needs for sorting. */
interface PlayerRowSortFields {
  player: {
    number?: number | null
    firstName?: string
    lastName?: string
    position?: string | null
  }
  stats?: unknown
}

// Sort order is the index of the first positionFilter whose canonical list
// includes this player's resolved position. Unknown positions sort last.
export function getPositionOrder(
  position: string | null | undefined,
  config: ReportingSportConfig
): number {
  if (!position) return 999
  const canonical = config.resolvePosition(position)
  if (!canonical) return 999
  for (let i = 0; i < config.positionFilters.length; i++) {
    const filter = config.positionFilters[i]
    if (filter.canonical.includes(canonical)) return i
  }
  return 999
}

/**
 * Compare two player rows by last name (case-insensitive, ascending).
 */
export function lastNameCmp(
  a: PlayerRowSortFields,
  b: PlayerRowSortFields
): number {
  return (a.player.lastName || '')
    .toLowerCase()
    .localeCompare((b.player.lastName || '').toLowerCase())
}

/**
 * Sort player rows based on the active sort field and direction.
 *
 * Sort modes:
 * - 'number' or 'position': position group order (GKP → DEF → MID → FWD), then last name A-Z
 * - 'player': last name A-Z, then first name A-Z
 * - any other field: numeric stat value (using getStatValue), nulls treated as 0
 */
export function sortPlayerRows<T extends PlayerRowSortFields>(
  rows: T[],
  sortField: string,
  sortDir: SortDir,
  config: ReportingSportConfig
): T[] {
  const sorted = [...rows]

  if (sortField === 'number' || sortField === 'position') {
    sorted.sort((a, b) => {
      const posA = getPositionOrder(a.player.position, config)
      const posB = getPositionOrder(b.player.position, config)
      const posDiff = posA - posB
      if (posDiff !== 0) return sortDir === 'asc' ? posDiff : -posDiff
      return lastNameCmp(a, b)
    })
  } else if (sortField === 'player') {
    sorted.sort((a, b) => {
      const lastA = (a.player.lastName || '').toLowerCase()
      const lastB = (b.player.lastName || '').toLowerCase()
      const diff =
        lastA.localeCompare(lastB) ||
        (a.player.firstName || '')
          .toLowerCase()
          .localeCompare((b.player.firstName || '').toLowerCase())
      return sortDir === 'asc' ? diff : -diff
    })
  } else {
    sorted.sort((a, b) => {
      const statsA = a.stats as Record<string, unknown> | undefined
      const statsB = b.stats as Record<string, unknown> | undefined
      const va = getStatValue(statsA?.[sortField] as StatInput) ?? 0
      const vb = getStatValue(statsB?.[sortField] as StatInput) ?? 0
      return sortDir === 'asc' ? va - vb : vb - va
    })
  }

  return sorted
}

/**
 * Format a season average value with percentage of session value.
 *
 * Shows "avgValue (pct%)" where pct = (sessionValue / seasonAvg) * 100.
 * When seasonAvg is 0 or value is null, shows just the average (no percentage).
 *
 * Note: In the Angular reporting tool, season averages for single-session teams
 * appear to show different values — this is because Angular's createStats()
 * spreads Math.random() mock data as defaults before real data overwrites it.
 * Fields not overwritten retain random values. Our tool correctly shows
 * value === seasonAvg for single-session teams.
 */
export function formatSeasonAvg(
  value: number | null,
  stat: StatValue,
  unitType: ReportingUnitType | undefined,
  unitSystem: UnitSystem
): string {
  const avgStr = formatStatValue(stat.seasonAvg, unitType, unitSystem)
  if (stat.seasonAvg === 0 || value == null) return avgStr
  const pct = Math.round((value / stat.seasonAvg) * 100)
  return `${avgStr} (${pct}%)`
}
